import { Text } from 'blaze-ui/Text/Text'
import Styles from './WeeklyBudgetInput.module.scss'
import cx from 'classnames'
import CurrencyInput from 'react-currency-input-field'
import { useCallback, useMemo } from 'react'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'

interface WeeklyBudgetInputProps {
  value?: number
  onChange?: (value: number) => void
  disabled?: boolean
}

export const WeeklyBudgetInput = ({ value, onChange, disabled = false }: WeeklyBudgetInputProps) => {
  const { data } = useExternalIntegrations()

  const currencyCode = useMemo<string>(() => {
    const externalIntegrations = data?.external_integrations ?? []
    const adIntegration = externalIntegrations.find(
      (integration: ExternalIntegration) => integration.provider === ExternalIntegrationEnum.MetaAds,
    )

    const additionalData = adIntegration?.additional_data
    if (additionalData && 'currency' in additionalData) {
      return additionalData?.currency ?? 'USD'
    } else {
      return 'USD'
    }
  }, [data])

  const handleValueChange = useCallback(
    (value: string | undefined) => {
      if (!onChange || disabled) return

      const newValue = value ? parseInt(value, 10) : 0
      if (isFinite(newValue) && newValue >= 0) {
        onChange(newValue)
      }
    },
    [onChange, disabled],
  )

  const label = useMemo(() => (currencyCode ? `Weekly budget in ${currencyCode}` : 'Weekly budget'), [currencyCode])

  return (
    <div className={Styles.root}>
      <div className={Styles.labelContainer}>
        <Text variant="secondary" color="var(--dark-40)">
          {label}
        </Text>
      </div>
      <div className={cx(Styles.inputContainer, { [Styles.disabled]: disabled })}>
        <CurrencyInput
          disabled={disabled}
          allowNegativeValue={false}
          allowDecimals={false}
          className={cx(Styles.currencyInput, { [Styles.disabled]: disabled })}
          intlConfig={{ locale: 'en-US', currency: currencyCode ?? undefined }}
          defaultValue={value ?? 0}
          onValueChange={handleValueChange}
          aria-label={label}
        />
      </div>
    </div>
  )
}
